import { Link } from "react-router-dom";
import {
  Accordion,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Progress,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconCheck,
  IconCircle,
  IconCircleCheck,
  IconCircleDashed,
  IconLock,
} from "@tabler/icons-react";
import { SETUP_STATE } from "@congrs/schemas";

export const SETTINGS_LINK = "/app/events/settings?tab=general";

const TASK_LINKS = {
  "essentials.title": SETTINGS_LINK,
  "essentials.language": SETTINGS_LINK,
  "details.dates": SETTINGS_LINK,
  "details.location": SETTINGS_LINK,
  "details.contact": SETTINGS_LINK,
  "brand.accentColor": "/app/events/settings?tab=customization",
  "brand.logo": "/app/events/settings?tab=customization",
  "brand.header": "/app/events/settings?tab=customization",
  "access.holders": "/app/tickets",
  "access.ticketTypes": "/app/registrations/form?tab=ticketTypes",
  "access.registrationOpen": "/app/registrations/form?tab=settings",
  "agenda.locations": "/app/sessions/locations",
  "agenda.speakers": "/app/speakers",
  "agenda.sponsorLevels": "/app/sponsors/levels",
  "agenda.sponsors": "/app/sponsors",
  "agenda.sessions": "/app/sessions",
  "engage.tags": "/app/tags",
  "engage.pages": "/app/pages",
  "engage.announcements": "/app/announcements",
  "engage.polls": "/app/polls",
  "engage.surveys": "/app/surveys",
};

export const BORDER = "1px solid var(--mantine-color-default-border)";

const isResolved = (state) =>
  state === SETUP_STATE.DONE || state === SETUP_STATE.SKIPPED;

const blockerText = (blockedBy, labels) =>
  `Needs ${blockedBy.map((key) => labels[key] ?? key).join(", ")} first`;

function StepIcon({ step, index }) {
  if (step.state === SETUP_STATE.DONE) {
    return (
      <ThemeIcon radius="xl" size={28} color="green">
        <IconCheck size={16} />
      </ThemeIcon>
    );
  }
  if (step.state === SETUP_STATE.BLOCKED) {
    return (
      <ThemeIcon radius="xl" size={28} variant="light" color="gray">
        <IconLock size={16} />
      </ThemeIcon>
    );
  }
  return (
    <ThemeIcon radius="xl" size={28} variant="outline" color="gray">
      <Text size="sm" fw={600}>
        {index + 1}
      </Text>
    </ThemeIcon>
  );
}

function TaskIcon({ state }) {
  if (state === SETUP_STATE.DONE)
    return <IconCircleCheck size={20} color="var(--mantine-color-green-6)" />;
  if (state === SETUP_STATE.BLOCKED)
    return <IconLock size={20} color="var(--mantine-color-gray-5)" />;
  if (state === SETUP_STATE.SKIPPED)
    return <IconCircleDashed size={20} color="var(--mantine-color-gray-5)" />;
  return <IconCircle size={20} color="var(--mantine-color-gray-5)" />;
}

function TaskRow({ task, labels, skipped, onToggleSkip, isSaving }) {
  const isDone = task.state === SETUP_STATE.DONE;
  const isBlocked = task.state === SETUP_STATE.BLOCKED;
  const isSkipped = task.state === SETUP_STATE.SKIPPED;
  const canSkip = task.skippable && !isDone && !isSkipped;
  const canUnskip = isSkipped && skipped.includes(task.key);

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      py="sm"
      style={{ borderTop: BORDER }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
        <Box style={{ flexShrink: 0 }}>
          <TaskIcon state={task.state} />
        </Box>
        <div>
          <Text
            size="sm"
            c={isResolved(task.state) ? "dimmed" : undefined}
            td={isDone ? "line-through" : undefined}
          >
            {task.label}
          </Text>
          {task.hint && !isDone && (
            <Text size="xs" c="dimmed">
              {task.hint}
            </Text>
          )}
          {isBlocked && (
            <Text size="xs" c="orange.7">
              {blockerText(task.blockedBy, labels)}
            </Text>
          )}
        </div>
      </Group>
      <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
        {task.required && !isDone && (
          <Badge variant="outline" color="gray" size="sm" radius="sm">
            Required
          </Badge>
        )}
        {isSkipped && (
          <Badge variant="light" color="gray" size="sm" radius="sm">
            Skipped
          </Badge>
        )}
        {(canSkip || canUnskip) && (
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            disabled={isSaving}
            onClick={() => onToggleSkip(task.key)}
          >
            {canUnskip ? "Undo skip" : "Skip"}
          </Button>
        )}
        <Button
          component={Link}
          to={TASK_LINKS[task.key]}
          size="xs"
          variant={isDone ? "subtle" : "default"}
          disabled={isBlocked}
        >
          {isDone ? "Edit" : "Set up"}
        </Button>
      </Group>
    </Group>
  );
}

function StepItem({ step, index, labels, skipped, onToggleSkip, isSaving }) {
  const isStepSkipped = step.state === SETUP_STATE.SKIPPED;

  return (
    <Accordion.Item value={step.key}>
      <Accordion.Control>
        <Group justify="space-between" wrap="nowrap" pr="sm">
          <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
            <StepIcon step={step} index={index} />
            <div>
              <Text fw={600}>
                {step.label}
                {step.optional && (
                  <Text span c="dimmed" fw={400} size="sm">
                    {" "}
                    — optional
                  </Text>
                )}
              </Text>
              <Text size="sm" c="dimmed">
                {step.state === SETUP_STATE.BLOCKED
                  ? blockerText(step.blockedBy, labels)
                  : step.hint}
              </Text>
            </div>
          </Group>
          <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
            {step.requiredRemaining > 0 && (
              <Badge variant="outline" color="gray" size="sm" radius="sm">
                {step.requiredRemaining} required
              </Badge>
            )}
            {isStepSkipped && (
              <Badge variant="light" color="gray" size="sm" radius="sm">
                Skipped
              </Badge>
            )}
            <Text size="sm" c="dimmed">
              {step.done} of {step.total}
            </Text>
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap={0}>
          {step.tasks.map((task) => (
            <TaskRow
              key={task.key}
              task={task}
              labels={labels}
              skipped={skipped}
              onToggleSkip={onToggleSkip}
              isSaving={isSaving}
            />
          ))}
        </Stack>
        {step.optional && step.state !== SETUP_STATE.DONE && (
          <Group justify="flex-end" pt="xs" style={{ borderTop: BORDER }}>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              disabled={isSaving}
              onClick={() => onToggleSkip(step.key)}
            >
              {isStepSkipped ? "Undo skip step" : "Skip this step"}
            </Button>
          </Group>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function ProgressSummary({ totals }) {
  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb="xs">
        <Text size="sm">
          <b>{totals.done}</b> of {totals.total} tasks done
        </Text>
        <Text size="sm" c={totals.requiredRemaining ? "dimmed" : "green"}>
          {totals.requiredRemaining
            ? `${totals.requiredRemaining} required left`
            : "All required tasks done"}
        </Text>
      </Group>
      <Progress
        value={totals.total ? (totals.done / totals.total) * 100 : 0}
        size="sm"
      />
    </Paper>
  );
}

function DraftBanner({ requiredRemaining }) {
  return (
    <Paper
      withBorder
      radius="md"
      px="md"
      py="sm"
      bg="var(--mantine-color-default-hover)"
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <IconLock size={22} style={{ flexShrink: 0 }} />
          <div>
            <Text fw={600}>
              Your event is in Draft — attendees can&apos;t see it yet
            </Text>
            <Text size="sm" c="dimmed">
              {requiredRemaining
                ? `You can go live anytime, but ${requiredRemaining} required ${requiredRemaining === 1 ? "task is" : "tasks are"} still open.`
                : "All required tasks are done — you're ready to go live."}
            </Text>
          </div>
        </Group>
        <Button component={Link} to={SETTINGS_LINK} style={{ flexShrink: 0 }}>
          Go live
        </Button>
      </Group>
    </Paper>
  );
}

export default function SetupChecklist({
  progress,
  skipped,
  onToggleSkip,
  isSaving,
}) {
  const labels = Object.fromEntries(
    progress.steps.flatMap((step) => [
      [step.key, step.label],
      ...step.tasks.map((task) => [task.key, task.label]),
    ])
  );
  const firstOpen = progress.steps.find((step) => !isResolved(step.state))?.key;

  return (
    <>
      <ProgressSummary totals={progress.totals} />
      {progress.event.status === "draft" && (
        <DraftBanner requiredRemaining={progress.totals.requiredRemaining} />
      )}
      <Accordion
        variant="separated"
        radius="md"
        defaultValue={firstOpen ?? null}
        styles={{
          item: {
            backgroundColor: "var(--mantine-color-body)",
            border: BORDER,
          },
        }}
      >
        {progress.steps.map((step, index) => (
          <StepItem
            key={step.key}
            step={step}
            index={index}
            labels={labels}
            skipped={skipped}
            onToggleSkip={onToggleSkip}
            isSaving={isSaving}
          />
        ))}
      </Accordion>
    </>
  );
}
